// The wizard's page list <-> PUT /assignments/{id}/draftPages.
//
// Submissions do not exist until the Organise commit, so until then nothing
// references the uploaded files and a refresh loses the page list: order,
// deletions, labels, manual typed pages and the routing. The files were never at
// risk — the list of them was. This is that list, in a shape the server can hold.
//
// Two numbers exist on a page and only one of them is identity:
//
//   sourcePageIndex — 0-based position INSIDE its own file. Set once at
//     buildPagesForFiles, unchanged by reorder or delete, sent as `pageIndex` at
//     commit and range-checked against UploadedFile.pageCount. Persisted.
//   pageNumber — 1-based position in the COMBINED cross-file array, recomputed
//     from array index on every mutation. Display only (the badge overlay, alt
//     text, "Pages: 3, 4, 7"). NOT persisted: array order already carries it, and
//     a stored copy could only ever disagree with the array every consumer reads.
//
// Page ids round-trip verbatim. studentAssignments is keyed by page id, so a
// regenerated id silently drops that page's routing.
import { PLACEHOLDER_THUMBNAIL } from './thumbnails';
import { getFileType } from './fileTypes';
import { groupBy } from './groupBy';
import {
  isManual,
  pageIndexOf,
  manualTextOf,
  fileNameOf,
  isExpressible,
} from './pageRules';

const toSnapshotFilePage = (page) => ({
  id: page.id,
  source: 'file',
  fileId: page.fileId,
  fileName: fileNameOf(page),
  fileUrl: page.fileUrl,
  pageIndex: pageIndexOf(page),
  label: page.label || '',
});

const toSnapshotManualPage = (page, text) => ({
  id: page.id,
  source: 'manual',
  fileId: page.fileId,
  label: page.label || '',
  text,
});

const toSnapshotPage = (page) =>
  isManual(page)
    ? toSnapshotManualPage(page, manualTextOf(page))
    : toSnapshotFilePage(page);

/** @returns {Array} the page list as the server holds it, order preserved. */
export const toSnapshotPages = (pages) =>
  (pages || []).filter(isExpressible).map(toSnapshotPage);

/** pageNumber is a pure function of array position — see the note above. */
export const renumberPages = (pages) =>
  (pages || []).map((page, index) => ({ ...page, pageNumber: index + 1 }));

const fromSnapshotManualPage = (page) => ({
  id: page.id,
  fileId: page.fileId,
  source: 'manual',
  sourcePageIndex: 0,
  label: page.label || '',
  content: page.text || '',
  thumbnail: PLACEHOLDER_THUMBNAIL,
});

// No thumbnail is stored (a data URL per page would dwarf the rest of the
// snapshot), so restored pages open on the placeholder and are re-rendered from
// their fileUrl afterwards — see hooks/useResumedThumbnails.
const fromSnapshotFilePage = (page) => ({
  id: page.id,
  fileId: page.fileId,
  source: 'file',
  sourcePageIndex: Number(page.pageIndex) || 0,
  fileUrl: page.fileUrl,
  fileName: page.fileName || '',
  label: page.label || '',
  thumbnail: PLACEHOLDER_THUMBNAIL,
});

/** @returns {Array} wizard pages, renumbered from array position. */
export const fromSnapshotPages = (snapshotPages) =>
  renumberPages(
    (snapshotPages || []).map((page) =>
      isManual(page) ? fromSnapshotManualPage(page) : fromSnapshotFilePage(page),
    ),
  );

const fileEntryFor = (page, pageCount) => ({
  id: page.fileId,
  name: isManual(page) ? page.label || 'Manual response' : fileNameOf(page),
  type: isManual(page) ? 'manual' : getFileType(fileNameOf(page)),
  source: isManual(page) ? 'manual' : 'file',
  fileUrl: isManual(page) ? null : page.fileUrl,
  pageCount,
  thumbnails: [],
  // The bytes are gone after a reload. Anything that needs them has to work from
  // fileUrl instead.
  file: null,
});

/**
 * The left rail, rebuilt from the pages that survived — `pageCount` is how many
 * are still there, not how long the original file was, so a file the teacher
 * pruned to two pages comes back as two.
 *
 * @returns {Array} file entries in first-appearance order.
 */
export const filesFromSnapshotPages = (snapshotPages) =>
  groupBy(
    (snapshotPages || []).filter((page) => page?.fileId),
    (page) => page.fileId,
  ).map(([, pages]) => fileEntryFor(pages[0], pages.length));
